import { findKey } from "lodash";

import {
  useConnectionWithCredentialsQuery,
  useIntegrationConfigurationQuery,
} from "@/apollo/types";
import { WeldTab } from "@/components/elements/tabs";
import { IntegrationLogo } from "@/integrations";
import type { LocationGenerics } from "@/router/locationGenerics";
import { LinkWithSlug } from "@/router";
import { Tab } from "@headlessui/react";
import { Outlet, useMatch, useMatchRoute } from "@tanstack/react-location";

import { SettingsPageLayout } from "../Settings/PageLayout";

export function ViewConnectionLayout() {
  const match = useMatch<LocationGenerics>();
  const matchRoute = useMatchRoute();

  const { data } = useConnectionWithCredentialsQuery({
    variables: {
      id: match.params.connectionId,
    },
  });
  const connection = data?.connection;

  const { data: integrationData } = useIntegrationConfigurationQuery({
    variables: {
      id: connection?.integrationId ?? "",
    },
    skip: !connection?.integrationId,
  });

  if (!connection) {
    return null;
  }

  const tabRoutes = { 1: "./configuration", 2: "./authorization-link" };
  const selectedIndex = parseInt(
    findKey(tabRoutes, (route) => matchRoute({ to: route })) ?? "0",
  );

  return (
    <SettingsPageLayout
      renderHeader={() => {
        return (
          <header>
            <div className="flex min-h-10 items-center gap-3 text-xl">
              <LinkWithSlug
                to={`/settings/connections`}
                className="whitespace-nowrap opacity-50 hover:underline focus:outline-hidden dark:hover:text-gray-300"
              >
                Connectors
              </LinkWithSlug>
              <Slash />
              <div className="inline-flex items-center gap-3">
                <IntegrationLogo
                  id={connection.integrationId}
                  className="h-5"
                />
                {connection.label}
              </div>
            </div>
          </header>
        );
      }}
    >
      <Tab.Group
        selectedIndex={selectedIndex}
        as="div"
        className="flex h-full flex-col"
      >
        <Tab.List className="mb-6 flex gap-8">
          <WeldTab
            as={LinkWithSlug}
            to={`/settings/connections/${connection.id}`}
            id="overview"
            key="overview"
            groupId="view-connection-layout"
          >
            Overview
          </WeldTab>
          {integrationData?.integrationConfiguration.requiresConfiguration && (
            <>
              <WeldTab
                as={LinkWithSlug}
                to={`/settings/connections/${connection.id}/configuration`}
                id="configuration"
                key="configuration"
                groupId="view-connection-layout"
              >
                Configuration
              </WeldTab>
              <WeldTab
                as={LinkWithSlug}
                to={`/settings/connections/${connection.id}/authorization-link`}
                id="authorization-link"
                key="authorization-link"
                groupId="view-connection-layout"
              >
                Share Authorization Link
              </WeldTab>
            </>
          )}
        </Tab.List>
        <Tab.Panels className="grow">
          <Tab.Panel className="h-full">
            <Outlet />
          </Tab.Panel>
          <Tab.Panel className="h-full">
            <Outlet />
          </Tab.Panel>
          <Tab.Panel className="h-full">
            <Outlet />
          </Tab.Panel>
        </Tab.Panels>
      </Tab.Group>
    </SettingsPageLayout>
  );
}

const Slash = () => (
  <svg
    className="size-5 shrink-0 fill-current text-gray-300"
    viewBox="0 0 20 20"
    aria-hidden="true"
  >
    <path d="M5.555 17.776l8-16 .894.448-8 16-.894-.448z" />
  </svg>
);
